/**
 * The one-time conversion to ml (docs/17-getraenke.md, Umstellung bestehender Daten; F18): before,
 * a wine counted in bottles and a receipt carried a price per bottle. Now every drink counts in ml,
 * with the bottle as its container, and a receipt carries the amount for its whole quantity.
 *
 * Bookings are otherwise never changed; this conversion is the one exception, before real use. The
 * server converts its databases with it (packages/server/src/db.ts), the device its waiting changes,
 * and an export from before is converted when it is read back.
 */
import type { Entity, EntityChange } from "./entities.ts";
import type { Booking, Id } from "./model.ts";
import type { InventoryChange, InventoryReport, InventoryRow } from "./inventory.ts";
import { sizeOf } from "./drinks.ts";
import { isProfiled } from "./profile-wine.ts";

/** Version of the exchange between device and server; devices of an older one must update first. */
export const PROTOCOL = 2;

/** The header a device sends it in. */
export const PROTOCOL_HEADER = "x-lagerlog-protocol";

/** Counted in bottles before F18: unit „Fl.“, or a wine of the profile without a unit. */
const inBottles = (f: Record<string, unknown>) => f.unit === "Fl." || (f.unit === undefined && isProfiled(f));

/** ml per old base unit: the bottle size for an article counted in bottles (0,75 l when unknown), else 1. */
export function mlFactor(f: Record<string, unknown>): number {
  return inBottles(f) ? (sizeOf(f) ?? 750) : 1;
}

/**
 * The fields of an article that change: unit ml, size as a number of ml, packs in ml, the profile
 * (wine). Empty when nothing changes (already in ml, or not a drink).
 */
export function articleToMl(f: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (typeof f.size === "string") {
    const ml = sizeOf(f);
    if (ml !== null) out.size = ml;
  }
  if (!inBottles(f)) return out;
  const factor = mlFactor(f);
  out.unit = "ml";
  out.size = factor;
  if (Array.isArray(f.packs)) out.packs = (f.packs as { factor?: unknown }[]).map((p) => (typeof p?.factor === "number" ? { ...p, factor: p.factor * factor } : p));
  if (isProfiled(f) && f.profile === undefined) out.profile = "wein";
  return out;
}

/**
 * An entity converted: an article's fields, the frozen report of a closed inventory (its
 * quantities per wine). Clocks of new fields take the latest clock of the entity.
 */
export function entityToMl(e: Entity, factorOf: (article: Id) => number): Entity {
  if (e.type === "inventory") return inventoryToMl(e, factorOf);
  if (e.type !== "article") return e;
  const changed = articleToMl(e.fields);
  if (Object.keys(changed).length === 0) return e;
  const latest = Object.values(e.clocks).reduce<[number, string]>((a, c) => (c[0] > a[0] || (c[0] === a[0] && c[1] > a[1]) ? c : a), [0, ""]);
  const clocks = { ...e.clocks };
  for (const k of Object.keys(changed)) clocks[k] ??= latest;
  return { ...e, fields: { ...e.fields, ...changed }, clocks };
}

function inventoryToMl(e: Entity, factorOf: (article: Id) => number): Entity {
  const report = e.fields.report as InventoryReport | undefined;
  const changes = e.fields.changes as InventoryChange[] | undefined;
  if (!report && !changes) return e;
  const fields = { ...e.fields };
  if (report && Array.isArray(report.rows)) {
    const rows = report.rows.map((r) => {
      const k = factorOf(r.article);
      return { ...r, counted: r.counted * k, expected: r.expected * k, diff: r.diff * k, rest: r.rest * k, causes: r.causes.map((c) => ({ ...c, qty: c.qty * k })) };
    });
    const sum = (f: (r: InventoryRow) => number) => rows.reduce((s, r) => s + f(r), 0);
    fields.report = {
      ...report,
      rows,
      uncounted: (report.uncounted ?? []).map((u) => ({ ...u, qty: u.qty * factorOf(u.article) })),
      totals: { ...report.totals, diff: sum((r) => r.diff), causes: sum((r) => r.causes.reduce((s, c) => s + c.qty, 0)), rest: sum((r) => r.rest) },
    };
  }
  if (Array.isArray(changes)) fields.changes = changes.map((c) => ({ ...c, before: c.before * factorOf(c.article), after: c.after * factorOf(c.article) }));
  return { ...e, fields };
}

/** A waiting change of an article, converted against what the device knows of it (`known`). */
export function changeToMl(c: EntityChange, known: Record<string, unknown> | undefined): EntityChange {
  if (c.type !== "article") return c;
  const changed = articleToMl({ ...known, ...c.fields });
  const fields = { ...c.fields };
  for (const [k, v] of Object.entries(changed)) if (k in c.fields || k === "unit" || known === undefined) fields[k] = v;
  return { ...c, fields };
}

type Legacy = Booking & { unitPrice?: number };

/** A booking in ml: quantities times the factor of their article, a price per unit as the amount. */
export function bookingToMl(b: Booking, factorOf: (article: Id) => number): Booking {
  const legacy = b as Legacy;
  switch (b.kind) {
    case "in": {
      const { unitPrice, ...rest } = legacy as Extract<Legacy, { kind: "in" }>;
      const amount = unitPrice !== undefined ? Math.round(unitPrice * b.qty) : b.amount;
      return { ...rest, qty: b.qty * factorOf(b.article), ...(amount !== undefined ? { amount } : {}) };
    }
    case "out":
    case "move":
    case "count":
      return { ...b, qty: b.qty * factorOf(b.article) };
    case "convert":
      return { ...b, fromQty: b.fromQty * factorOf(b.fromArticle), toQty: b.toQty * factorOf(b.toArticle) };
    case "cancel":
      return b;
  }
}

/** Factors per article from entities as they were before the conversion. */
export function factorsOf(entities: Iterable<{ type: string; id: Id; fields: Record<string, unknown> }>): (article: Id) => number {
  const map = new Map<Id, number>();
  for (const e of entities) if (e.type === "article") map.set(e.id, mlFactor(e.fields));
  return (article) => map.get(article) ?? 1;
}
